import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Download, MessageSquare, Upload } from 'lucide-react';
import { PageLoadingSkeleton } from '../components/PageLoadingSkeleton';
import { getUserFacingError } from '../lib/errors';
import { formatCurrency } from '../lib/formatters';
import { getOrderFileSignedUrl } from '../lib/marketplace';

const initialReview = {
  rating: '5',
  headline: '',
  body: '',
};

function formatOrderStatus(status) {
  return String(status || '')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatTimestamp(value) {
  if (!value) {
    return 'Not set';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Not set';
  }

  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function OrderPage({
  currentUser,
  snapshot,
  loading,
  onLoadOrder,
  onSendMessage,
  onUploadFile,
  onUpdateOrderStatus,
  onSubmitReview,
}) {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [uploadKind, setUploadKind] = useState('attachment');
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(initialReview);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewExitModalOpen, setReviewExitModalOpen] = useState(false);
  const [reviewPromptDismissed, setReviewPromptDismissed] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const reviewSuccessTimerRef = useRef(null);

  const snapshotOrder = useMemo(() => {
    if (!snapshot?.orders) {
      return null;
    }

    return snapshot.orders.find((entry) => entry.id === orderId) || null;
  }, [orderId, snapshot]);

  useEffect(() => {
    let active = true;

    async function loadOrder() {
      if (!currentUser || !onLoadOrder || !orderId) {
        return;
      }

      try {
        const result = await onLoadOrder(orderId);
        if (active) {
          setOrder(result?.order || null);
        }
      } catch (error) {
        if (active) {
          setStatus(getUserFacingError(error, 'Unable to load this order right now.'));
        }
      }
    }

    if (snapshotOrder) {
      setOrder(snapshotOrder);
    }

    loadOrder();

    return () => {
      active = false;
    };
  }, [currentUser, onLoadOrder, orderId, snapshotOrder]);

  useEffect(() => {
    if (
      order?.canLeaveReview &&
      !order.review &&
      !reviewPromptDismissed &&
      !reviewModalOpen &&
      !reviewExitModalOpen
    ) {
      setReviewModalOpen(true);
    }
  }, [order, reviewExitModalOpen, reviewModalOpen, reviewPromptDismissed]);

  useEffect(() => {
    return () => {
      if (reviewSuccessTimerRef.current) {
        window.clearTimeout(reviewSuccessTimerRef.current);
      }
    };
  }, []);

  if (!currentUser) {
    return <Navigate replace to="/login" />;
  }

  if (loading || !snapshot) {
    return <PageLoadingSkeleton label="Loading order" variant="compact" />;
  }

  if (!order) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>Order not found</h1>
          <p>This order does not exist or is not available for your account.</p>
        </div>
      </section>
    );
  }

  const pricingLine =
    order.pricingType === 'subscription'
      ? `${formatCurrency(order.totalAmount)} / ${order.billingInterval || 'month'}`
      : `${formatCurrency(order.totalAmount)} one time`;

  const showDeliverAction = order.role === 'seller' && ['active', 'missing_details', 'revision_requested'].includes(order.status);
  const showCompleteAction = order.role === 'buyer' && order.status === 'delivered';

  async function handleSendMessage(event) {
    event.preventDefault();
    setStatus('Sending message...');

    try {
      await onSendMessage(order.id, messageBody);
      const result = await onLoadOrder(order.id);
      setOrder(result?.order || null);
      setMessageBody('');
      setStatus('Message sent.');
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to send that message. Please try again.'));
    }
  }

  async function handleUploadFile(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploading(true);
    setStatus('Uploading file...');

    try {
      await onUploadFile(order.id, file, uploadKind);
      const result = await onLoadOrder(order.id);
      setOrder(result?.order || null);
      setStatus('File uploaded.');
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to upload that file. Please try again.'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleOrderStatus(nextStatus) {
    setStatus(nextStatus === 'delivered' ? 'Marking order delivered...' : 'Completing order...');

    try {
      await onUpdateOrderStatus(order.id, nextStatus);
      const result = await onLoadOrder(order.id);
      const nextOrder = result?.order || null;
      setOrder(nextOrder);
      setStatus(nextStatus === 'delivered' ? 'Order marked delivered.' : 'Order completed.');
      if (nextStatus === 'completed' && nextOrder?.canLeaveReview) {
        setReviewPromptDismissed(false);
        setReviewExitModalOpen(false);
        setReviewModalOpen(true);
      }
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to update this order right now.'));
    }
  }

  async function handleReviewSubmit(event) {
    event.preventDefault();
    setReviewSubmitting(true);
    setStatus('Submitting review...');

    try {
      await onSubmitReview(order.id, {
        rating: Number(review.rating),
        headline: review.headline,
        body: review.body,
      });
      const result = await onLoadOrder(order.id);
      setOrder(result?.order || null);
      setReview(initialReview);
      setReviewExitModalOpen(false);
      setReviewModalOpen(true);
      setReviewPromptDismissed(true);
      setReviewSuccess(true);
      setStatus('');
      if (reviewSuccessTimerRef.current) {
        window.clearTimeout(reviewSuccessTimerRef.current);
      }
      reviewSuccessTimerRef.current = window.setTimeout(() => {
        setReviewSuccess(false);
        setReviewModalOpen(false);
      }, 2300);
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to submit that review. Please try again.'));
    } finally {
      setReviewSubmitting(false);
    }
  }

  function handleReviewModalClose() {
    if (reviewSuccess || reviewSubmitting) {
      return;
    }
    setReviewModalOpen(false);
    setReviewExitModalOpen(true);
  }

  function handleSkipReview() {
    setReviewExitModalOpen(false);
    setReviewPromptDismissed(true);
  }

  function renderReviewFields(submitLabel) {
    return (
      <>
        <select
          className="ui-select"
          value={review.rating}
          onChange={(event) => setReview((current) => ({ ...current, rating: event.target.value }))}
        >
          <option value="5">5 stars</option>
          <option value="4">4 stars</option>
          <option value="3">3 stars</option>
          <option value="2">2 stars</option>
          <option value="1">1 star</option>
        </select>
        <input
          className="ui-input"
          type="text"
          placeholder="Short headline"
          value={review.headline}
          onChange={(event) => setReview((current) => ({ ...current, headline: event.target.value }))}
        />
        <textarea
          className="ui-textarea"
          rows="4"
          placeholder="Describe how the order went."
          value={review.body}
          onChange={(event) => setReview((current) => ({ ...current, body: event.target.value }))}
          required
        />
        <button type="submit" className="primary-button ui-button" disabled={reviewSubmitting}>
          {reviewSubmitting ? 'Submitting...' : submitLabel}
        </button>
      </>
    );
  }

  async function handleFileDownload(storagePath, fileName) {
    setStatus('Preparing download...');

    try {
      const result = await getOrderFileSignedUrl(storagePath);
      const response = await fetch(result.url);
      if (!response.ok) {
        throw new Error('Unable to download file.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || storagePath.split('/').pop()?.replace(/^\d+-/, '') || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      setStatus('');
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to prepare that download. Please try again.'));
    }
  }

  async function handleFileOpen(storagePath) {
    setStatus('Opening file...');

    try {
      const result = await getOrderFileSignedUrl(storagePath);
      window.open(result.url, '_blank', 'noopener,noreferrer');
      setStatus('');
    } catch (error) {
      setStatus(getUserFacingError(error, 'Unable to open that file. Please try again.'));
    }
  }

  return (
    <section className="page-shell order-page">
      <div className="listing-breadcrumbs">
        <Link to="/account" className="breadcrumb-link">
          Account
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current" aria-current="page">
          Order {order.id}
        </span>
      </div>

      <div className="detail-grid order-detail-grid">
        <div className="detail-main">
          <section className="detail-panel order-summary-panel">
            <div className="section-head">
              <div>
                <h2>{order.listing?.title || 'Order'}</h2>
                <p>{order.projectBrief || 'Order created on the marketplace.'}</p>
              </div>
              <span className={`account-order-status status-${order.status}`}>{formatOrderStatus(order.status)}</span>
            </div>

            <div className="detail-columns">
              <div>
                <span className="metric-label">Order ID</span>
                <strong>{order.id}</strong>
              </div>
              <div>
                <span className="metric-label">{order.role === 'seller' ? 'Buyer' : 'Seller'}</span>
                <strong>{order.counterparty?.name || 'Marketplace user'}</strong>
              </div>
              <div>
                <span className="metric-label">Pricing</span>
                <strong>{pricingLine}</strong>
              </div>
              <div>
                <span className="metric-label">Due</span>
                <strong>{formatTimestamp(order.dueDate)}</strong>
              </div>
            </div>

            <div className="order-status-actions">
              {showDeliverAction ? (
                <button type="button" className="primary-button ui-button" onClick={() => handleOrderStatus('delivered')}>
                  Mark delivered
                </button>
              ) : null}
              {showCompleteAction ? (
                <button type="button" className="primary-button ui-button" onClick={() => handleOrderStatus('completed')}>
                  Mark complete
                </button>
              ) : null}
              {order.listing?.slug ? (
                <Link to={`/agents/${order.listing.slug}`} className="ghost-button ui-button">
                  View agent
                </Link>
              ) : null}
            </div>
          </section>

          <section className="detail-panel">
            <div className="section-head">
              <h2>Messages</h2>
            </div>

            {order.messages.length === 0 ? (
              <p>No messages yet. Start the conversation below.</p>
            ) : (
              <div className="order-message-list order-detail-message-list">
                {order.messages.map((message) => (
                  <article
                    key={message.id}
                    className={`order-message-item${message.senderId === currentUser.id ? ' is-current-user' : ''}`}
                  >
                    <div className="order-message-top">
                      <strong>{message.senderId === currentUser.id ? 'You' : formatOrderStatus(message.senderRole)}</strong>
                      <span>{formatTimestamp(message.createdAt)}</span>
                    </div>
                    <p>{message.body}</p>
                  </article>
                ))}
              </div>
            )}

            <form className="ui-form order-message-form" onSubmit={handleSendMessage}>
              <textarea
                className="ui-textarea"
                rows="4"
                placeholder="Send an update, ask a question, or clarify delivery details."
                value={messageBody}
                onChange={(event) => setMessageBody(event.target.value)}
                required
              />
              <button type="submit" className="primary-button ui-button">
                <MessageSquare size={16} />
                Send message
              </button>
            </form>
          </section>

          <section className="detail-panel">
            <div className="section-head">
              <h2>Files</h2>
            </div>

            <div className="order-file-upload-row">
              <select className="ui-select" value={uploadKind} onChange={(event) => setUploadKind(event.target.value)}>
                <option value="attachment">Attachment</option>
                <option value="buyer_input">Buyer input</option>
                <option value="deliverable">Deliverable</option>
                <option value="reference">Reference</option>
              </select>
              <label className="ghost-button ui-button order-file-upload-button">
                <Upload size={16} />
                {uploading ? 'Uploading...' : 'Upload file'}
                <input type="file" hidden onChange={handleUploadFile} disabled={uploading} />
              </label>
            </div>

            {order.files.length === 0 ? (
              <p>No files have been uploaded to this order yet.</p>
            ) : (
              <div className="order-file-list">
                {order.files.map((file) => (
                  <article
                    key={file.id}
                    className="order-file-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFileOpen(file.storagePath)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleFileOpen(file.storagePath);
                      }
                    }}
                  >
                    <div className="order-file-copy">
                      <strong>{file.fileName}</strong>
                      <p>
                        {formatOrderStatus(file.fileKind)} by {formatOrderStatus(file.uploaderRole)} on {formatTimestamp(file.createdAt)}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="ghost-button ui-button order-file-download-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleFileDownload(file.storagePath, file.fileName);
                      }}
                    >
                      <Download size={16} />
                      Download
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>

          {order.review ? (
            <section className="detail-panel">
              <div className="section-head">
                <h2>Your review</h2>
              </div>
              <article className="review-card tone-blue">
                <p>"{order.review.body}"</p>
                <div className="review-person">
                  <div className="seller-avatar review-avatar">{order.review.reviewerName?.slice(0, 1) || 'R'}</div>
                  <div className="review-person-copy">
                    <strong>{order.review.reviewerName}</strong>
                    {order.review.company ? <span>{order.review.company}</span> : null}
                  </div>
                </div>
              </article>
            </section>
          ) : null}

          {status ? <p className="status-note">{status}</p> : null}
        </div>

        <aside className="detail-sidebar">
          <div className="detail-panel sticky-panel order-sidebar-panel">
            <h2>Order timeline</h2>
            <div className="sidebar-bullets">
              <span>Created {formatTimestamp(order.createdAt)}</span>
              <span>Updated {formatTimestamp(order.updatedAt)}</span>
              <span>Delivered {formatTimestamp(order.deliveredAt)}</span>
              <span>Completed {formatTimestamp(order.completedAt)}</span>
            </div>
          </div>
        </aside>
      </div>

      {reviewModalOpen ? (
        <div className="order-review-modal-backdrop" role="presentation">
          <section className="order-review-modal" role="dialog" aria-modal="true" aria-labelledby="order-review-title">
            {reviewSuccess ? (
              <div className="order-review-success" role="status" aria-live="polite">
                <div className="order-review-confetti" aria-hidden="true">
                  {Array.from({ length: 14 }, (_, index) => (
                    <span key={index} />
                  ))}
                </div>
                <h2 id="order-review-title">Thank you for the review</h2>
                <p>Your feedback was submitted and helps future buyers understand the seller's work.</p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="icon-only-button order-review-close"
                  aria-label="Close review prompt"
                  onClick={handleReviewModalClose}
                >
                  &times;
                </button>
                <h2 id="order-review-title">Leave a review</h2>
                <form className="ui-form order-review-form" onSubmit={handleReviewSubmit}>
                  {renderReviewFields('Submit review')}
                </form>
              </>
            )}
          </section>
        </div>
      ) : null}

      {reviewExitModalOpen ? (
        <div className="order-review-modal-backdrop" role="presentation">
          <section className="order-review-modal" role="dialog" aria-modal="true" aria-labelledby="review-exit-title">
            <h2 id="review-exit-title">Are you sure?</h2>
            <p>
              Reviews help buyers make better decisions and help sellers improve their service. A quick review also
              strengthens trust across the AgenticHire marketplace.
            </p>
            <form className="ui-form order-review-form" onSubmit={handleReviewSubmit}>
              <select
                className="ui-select"
                value={review.rating}
                onChange={(event) => setReview((current) => ({ ...current, rating: event.target.value }))}
              >
                <option value="5">5 stars</option>
                <option value="4">4 stars</option>
                <option value="3">3 stars</option>
                <option value="2">2 stars</option>
                <option value="1">1 star</option>
              </select>
              <input
                className="ui-input"
                type="text"
                placeholder="Short headline"
                value={review.headline}
                onChange={(event) => setReview((current) => ({ ...current, headline: event.target.value }))}
              />
              <textarea
                className="ui-textarea"
                rows="4"
                placeholder="Describe how the order went."
                value={review.body}
                onChange={(event) => setReview((current) => ({ ...current, body: event.target.value }))}
                required
              />
              <div className="order-review-confirm-actions">
                <button
                  type="button"
                  className="ghost-button ui-button order-review-skip-button"
                  onClick={handleSkipReview}
                  disabled={reviewSubmitting}
                >
                  I dont want to leave a review
                </button>
                <button type="submit" className="primary-button ui-button" disabled={reviewSubmitting}>
                  {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </section>
  );
}
